import React, { useEffect, useState, useCallback, useRef } from "react";
import { useChartsStore } from "../../store/chartStore";
import {
  getGroupedBarColorProviders,
  getBarColorProvider,
  DEFAULT_BAR_COLORS,
} from "../../Registry/BarColorProviders/barColorRegistry";
import { applyBarColorProvider } from "./barColorSelection.jsx";
import SimpleModal from "../Modals/SimpleModal";
import { barColorModalRef } from "./barColorModalRef";
import { isFeatureEnabled } from "../../data/Services/settings";
import "./BarColorModal.css";

// #112: cores de alta/baixa escolhidas pelo usuário — contraparte do
// "Propriedades da Série" do Silverlight. Só aparecem para métodos com semântica
// alta/baixa (supportsCustomColors); os métodos baseados em indicador têm paleta
// própria com significado e não são sobrescritos.
const CUSTOM_COLOR_FIELDS = [
  { key: "upStroke", label: "Barras de Alta" },
  { key: "downStroke", label: "Barras de Baixa" },
  { key: "upFill", label: "Preenchimento Barras de Alta" },
  { key: "downFill", label: "Preenchimento Barras de Baixa" },
];

// Shared "Cores das barras" modal — the ONE surface for picking a bar-color
// provider, opened by the navbar button AND by the chart context menu (via
// barColorModalRef). Replaces the cramped 130px navbar Dropdown / wrapped
// context-menu submenu: full labels, group headers, current-selection check.
// Selection goes through applyBarColorProvider (barColorSelection.jsx) — the
// exact navbar flow (missing-indicator confirm, incompatible-chart-type
// confirm, color-only cleanup). The modal closes BEFORE applying so those
// confirm dialogs are never stacked under it.
const BarColorModal = () => {
  const [visible, setVisible] = useState(false);
  // Editor de cores é um SEGUNDO modal, aberto pela engrenagem da linha. Ficar
  // embutido na lista a alongava até dar scroll, e como escolher um método FECHA
  // este modal, as cores do método recém-escolhido só apareciam depois de
  // reabrir. A engrenagem desacopla as duas ações.
  const [colorsVisible, setColorsVisible] = useState(false);
  const selectedChart = useChartsStore((state) => state.getSelectedChart());
  const chartId = selectedChart?.id || null;
  const currentProvider = selectedChart?.barColorProvider || "traditional";

  useEffect(() => {
    barColorModalRef.current = { open: () => setVisible(true) };
    return () => {
      barColorModalRef.current = null;
    };
  }, []);

  // Fechar a lista fecha o editor de cores junto — senão o segundo modal ficaria
  // órfão (o componente só renderiza enquanto a lista está visível).
  const handleHide = useCallback(() => {
    setVisible(false);
    setColorsVisible(false);
  }, []);

  // Cores custom do gráfico selecionado (#112). Lidas do config a cada render —
  // o modal fica montado entre trocas de gráfico.
  const customColors = selectedChart?.barColorConfig?.colors || {};
  const customColorsEnabled = isFeatureEnabled("barColorCustom");
  // O método ativo usa a paleta? Se não (Elder/RSI/Stochastic/MACD têm cores
  // próprias, com significado), a entrada continua acessível mas avisa que vale
  // para os métodos básicos — melhor que sumir e parecer que a opção não existe.
  const currentSupportsCustom =
    getBarColorProvider(currentProvider)?.supportsCustomColors === true;

  // O input de cor nativo dispara onChange a cada passo do arraste no seletor.
  // Cada escrita reprocessa TODAS as barras (barColorConfig entra na chave do
  // cache de dados), então gravar direto travaria o gráfico durante o gesto.
  // Estado local pinta o swatch na hora; o store recebe o valor assentado.
  const [pendingColors, setPendingColors] = useState({});
  // ACUMULA as pendências: o timer é único, então commitar só o último par
  // (chave, valor) perderia a cor anterior quando o usuário mexe em dois campos
  // dentro da janela do debounce — foi o que aconteceu na validação de fluxo.
  const pendingRef = useRef({});
  const commitTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    },
    [],
  );

  // Trocar de gráfico com o modal aberto não pode arrastar as cores pendentes do
  // gráfico anterior para a exibição do novo.
  useEffect(() => {
    pendingRef.current = {};
    setPendingColors({});
  }, [chartId]);

  const setCustomColor = useCallback((key, value) => {
    pendingRef.current = { ...pendingRef.current, [key]: value };
    setPendingColors(pendingRef.current);
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => {
      commitTimerRef.current = null;
      const batch = pendingRef.current;
      pendingRef.current = {};
      const chartsState = useChartsStore.getState();
      const chart = chartsState.getSelectedChart();
      if (!chart) return;
      const previous = chart.barColorConfig || {};
      chartsState.updateChartConfig(chart.id, {
        barColorConfig: {
          ...previous,
          colors: { ...(previous.colors || {}), ...batch },
        },
      });
    }, 150);
  }, []);

  const resetCustomColors = useCallback(() => {
    const chartsState = useChartsStore.getState();
    const chart = chartsState.getSelectedChart();
    if (!chart) return;
    // Remove só as cores — o resto do barColorConfig (config de outros métodos)
    // permanece.
    const { colors, ...rest } = chart.barColorConfig || {};
    void colors;
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    pendingRef.current = {};
    setPendingColors({});
    chartsState.updateChartConfig(chart.id, { barColorConfig: rest });
  }, []);

  const handlePick = useCallback((providerKey) => {
    setVisible(false);
    setColorsVisible(false);
    // Fresh chart state at click time (the modal can stay mounted across
    // chart/indicator changes).
    const chartsState = useChartsStore.getState();
    const chart = chartsState.getSelectedChart();
    if (!chart) return;
    applyBarColorProvider(chart.id, chart, providerKey);
  }, []);

  if (!visible || !chartId) {
    return null;
  }

  const groups = getGroupedBarColorProviders(selectedChart?.indicators || []);

  const listModal = (
    <SimpleModal
      title="Cores das barras"
      visible={visible}
      onHide={handleHide}
      footer={null}
      size="small"
      className="bar-color-modal"
    >
      <div className="bar-color-modal-content">
        {groups.map((group) => (
          <div key={group.label} className="bar-color-group">
            <div className="bar-color-group-title">{group.label}</div>
            {group.items.map((provider) => (
              <button
                key={provider.key}
                type="button"
                className={`bar-color-row${
                  currentProvider === provider.key ? " current" : ""
                }`}
                onClick={() => handlePick(provider.key)}
                data-evo-id={`bar-color-modal-${provider.key}`}
              >
                <span className="bar-color-row-check">
                  {currentProvider === provider.key && (
                    <i className="pi pi-check" />
                  )}
                </span>
                <span className="bar-color-row-label">
                  {provider.displayName}
                </span>
                {provider.isAvailable === false && (
                  <span className="bar-color-row-badge">Requer indicador</span>
                )}
              </button>
            ))}
          </div>
        ))}

        {/* UMA entrada de cores, não uma por método: a paleta é da SÉRIE e vale
            para os três métodos binários (é o modelo do Silverlight — o método
            escolhe a regra, as cores são do gráfico). Uma engrenagem por linha
            sugeriria três paletas independentes que não existem. */}
        {customColorsEnabled && (
          <div className="bar-color-group bar-color-colors-entry">
            <button
              type="button"
              className="bar-color-row"
              onClick={() => setColorsVisible(true)}
              data-evo-id="bar-color-modal-open-colors"
            >
              <span className="bar-color-row-check">
                <i className="pi pi-cog" />
              </span>
              <span className="bar-color-row-label">Cores de alta e baixa</span>
              {!currentSupportsCustom && (
                <span className="bar-color-row-hint">métodos básicos</span>
              )}
            </button>
          </div>
        )}
      </div>
    </SimpleModal>
  );

  const colorsModal = colorsVisible && (
    <SimpleModal
      title="Cores de alta e baixa"
      visible={colorsVisible}
      onHide={() => setColorsVisible(false)}
      size="small"
      className="bar-color-custom-modal"
      footer={
        <div className="modal-footer">
          <button
            type="button"
            className="bar-color-custom-reset"
            onClick={resetCustomColors}
            data-evo-id="bar-color-modal-custom-reset"
          >
            Padrão
          </button>
          <button
            type="button"
            className="bar-color-custom-close"
            onClick={() => setColorsVisible(false)}
            data-evo-id="bar-color-modal-custom-close"
          >
            Fechar
          </button>
        </div>
      }
    >
      <div className="bar-color-custom">
        {CUSTOM_COLOR_FIELDS.map(({ key, label }) => (
          <div key={key} className="bar-color-custom-row">
            <label
              className="bar-color-custom-label"
              htmlFor={`bar-color-${key}`}
            >
              {label}
            </label>
            {/* using copilot-instructions: use native color input instead of ColorPicker */}
            <input
              id={`bar-color-${key}`}
              type="color"
              className="bar-color-custom-input"
              value={
                pendingColors[key] ||
                customColors[key] ||
                DEFAULT_BAR_COLORS[key]
              }
              onChange={(e) => setCustomColor(key, e.target.value)}
              data-evo-id={`bar-color-modal-custom-${key}`}
            />
          </div>
        ))}
      </div>
    </SimpleModal>
  );

  return (
    <>
      {listModal}
      {colorsModal}
    </>
  );
};

export default BarColorModal;
